import { create } from 'zustand';
import type {
  Subject, StudyUnit, KnowledgePoint, Question, MockTest,
  MistakeEntry, Flashcard, StudentProgress, UploadedMaterial,
  AssessmentCriteria, DifficultyBreakdown, StudySession, QuestionPerformance
} from '../types';

// Demo subjects for Australian IB
const demoSubjects: Subject[] = [
  { id: 'bio-hl', name: '生物', nameEn: 'Biology', group: 'sciences', program: 'DP', level: 'HL', icon: '🧬' },
  { id: 'chem-sl', name: '化学', nameEn: 'Chemistry', group: 'sciences', program: 'DP', level: 'SL', icon: '⚗️' },
  { id: 'math-aa-hl', name: '数学AA', nameEn: 'Mathematics AA', group: 'mathematics', program: 'DP', level: 'HL', icon: '📐' },
  { id: 'eng-langlit', name: '英语语言与文学', nameEn: 'English Lang & Lit', group: 'language_literature', program: 'DP', level: 'HL', icon: '📝' },
  { id: 'econ-sl', name: '经济学', nameEn: 'Economics', group: 'individuals_societies', program: 'DP', level: 'SL', icon: '📊' },
  { id: 'chinese-ab', name: '中文B', nameEn: 'Chinese B', group: 'language_acquisition', program: 'DP', level: 'SL', icon: '🀄' },
];

const demoUnits: StudyUnit[] = [
  {
    id: 'bio-u1', title: '细胞生物学 Cell Biology', subjectId: 'bio-hl', order: 1, progress: 78,
    knowledgePoints: [
      { id: 'kp1', title: '细胞理论 Cell Theory', description: '细胞是生命的基本单位，所有生物由细胞组成', difficulty: 'foundation', subjectId: 'bio-hl', unitId: 'bio-u1', isExamFocus: true, isCommonMistake: false, connections: ['kp2', 'kp3'], mastery: 85 },
      { id: 'kp2', title: '原核与真核细胞 Prokaryotic & Eukaryotic', description: '两种细胞类型的结构与功能比较', difficulty: 'core', subjectId: 'bio-hl', unitId: 'bio-u1', isExamFocus: true, isCommonMistake: true, connections: ['kp1', 'kp4'], mastery: 62 },
      { id: 'kp3', title: '膜结构 Membrane Structure', description: '流动镶嵌模型与膜蛋白功能', difficulty: 'core', subjectId: 'bio-hl', unitId: 'bio-u1', isExamFocus: true, isCommonMistake: true, connections: ['kp1', 'kp5'], mastery: 45 },
      { id: 'kp4', title: '细胞分裂 Cell Division', description: '有丝分裂与减数分裂的过程与意义', difficulty: 'advanced', subjectId: 'bio-hl', unitId: 'bio-u1', isExamFocus: true, isCommonMistake: true, connections: ['kp2'], mastery: 38 },
      { id: 'kp5', title: '物质运输 Transport', description: '主动运输、被动运输与膜泡运输', difficulty: 'core', subjectId: 'bio-hl', unitId: 'bio-u1', isExamFocus: true, isCommonMistake: false, connections: ['kp3'], mastery: 70 },
    ]
  },
  {
    id: 'bio-u2', title: '分子生物学 Molecular Biology', subjectId: 'bio-hl', order: 2, progress: 45,
    knowledgePoints: [
      { id: 'kp6', title: '水的性质 Properties of Water', description: '氢键与水的生物学重要性', difficulty: 'foundation', subjectId: 'bio-hl', unitId: 'bio-u2', isExamFocus: false, isCommonMistake: false, connections: ['kp7'], mastery: 90 },
      { id: 'kp7', title: '碳水化合物与脂质', description: '单糖、多糖、脂肪酸结构与功能', difficulty: 'core', subjectId: 'bio-hl', unitId: 'bio-u2', isExamFocus: true, isCommonMistake: true, connections: ['kp6', 'kp8'], mastery: 55 },
      { id: 'kp8', title: '蛋白质 Proteins', description: '氨基酸结构、肽键、蛋白质四级结构', difficulty: 'advanced', subjectId: 'bio-hl', unitId: 'bio-u2', isExamFocus: true, isCommonMistake: true, connections: ['kp7'], mastery: 30 },
    ]
  },
  {
    id: 'bio-u3', title: '遗传学 Genetics', subjectId: 'bio-hl', order: 3, progress: 20,
    knowledgePoints: [
      { id: 'kp9', title: '基因与染色体 Genes & Chromosomes', description: '基因位于染色体上，等位基因的概念', difficulty: 'foundation', subjectId: 'bio-hl', unitId: 'bio-u3', isExamFocus: true, isCommonMistake: false, connections: ['kp10'], mastery: 60 },
      { id: 'kp10', title: '孟德尔遗传 Mendelian Genetics', description: '分离定律与自由组合定律', difficulty: 'core', subjectId: 'bio-hl', unitId: 'bio-u3', isExamFocus: true, isCommonMistake: true, connections: ['kp9'], mastery: 25 },
    ]
  },
];

const demoQuestions: Question[] = [
  {
    id: 'q1', content: 'Outline two differences between prokaryotic and eukaryotic cells. [4 marks]\n概述原核细胞和真核细胞的两个区别。',
    type: 'short_answer', tier: 'knowledge', knowledgePointIds: ['kp2'], marks: 4,
    answer: '1. Prokaryotic cells do not have a membrane-bound nucleus, while eukaryotic cells have a true nucleus. 2. Prokaryotic cells have 70S ribosomes while eukaryotic cells have 80S ribosomes.',
    markScheme: '每个正确区别 2 分（需要说明两种细胞的对比）。常见错误：仅描述一种细胞而未进行比较。',
    subjectId: 'bio-hl', unitId: 'bio-u1'
  },
  {
    id: 'q2', content: 'Explain the fluid mosaic model of membrane structure. [6 marks]\n解释细胞膜的流动镶嵌模型。',
    type: 'structured', tier: 'application', knowledgePointIds: ['kp3'], marks: 6,
    answer: 'The fluid mosaic model describes the cell membrane as a phospholipid bilayer with embedded proteins. The "fluid" refers to the lateral movement of phospholipids and proteins. The "mosaic" refers to the pattern created by scattered protein molecules.',
    markScheme: '磷脂双分子层描述 (2分) + 流动性解释 (2分) + 镶嵌蛋白质功能 (2分)。IB 评分重点：使用准确的科学术语。',
    subjectId: 'bio-hl', unitId: 'bio-u1'
  },
  {
    id: 'q3', content: 'Compare and contrast mitosis and meiosis in terms of their outcomes and significance. [8 marks]\n比较有丝分裂和减数分裂的结果和意义。',
    type: 'extended_response', tier: 'analysis', knowledgePointIds: ['kp4'], marks: 8,
    answer: 'Mitosis produces 2 genetically identical daughter cells while meiosis produces 4 genetically unique daughter cells. Mitosis maintains chromosome number while meiosis halves it. Mitosis is for growth/repair, meiosis is for gamete production.',
    markScheme: '结果比较 (3分) + 意义分析 (3分) + 专业术语使用 (2分)。注意：需要使用比较性语言如 whereas, in contrast 等。',
    subjectId: 'bio-hl', unitId: 'bio-u1'
  },
  {
    id: 'q4', content: 'Evaluate the evidence for the cell theory and discuss its limitations. [8 marks]\n评估细胞理论的证据并讨论其局限性。',
    type: 'extended_response', tier: 'evaluation', knowledgePointIds: ['kp1'], marks: 8,
    answer: 'Evidence includes microscope observations and experiments. Limitations: striated muscle fibres are not typical cells, fungi have continuous cytoplasm, and the first cells would have had to arise from non-living material.',
    markScheme: '证据列举 (3分) + 局限性讨论 (3分) + 评估性语言使用 (2分)。IB 要求：展示批判性思维，使用 "however", "nevertheless" 等连接词。',
    subjectId: 'bio-hl', unitId: 'bio-u1'
  },
];

const demoMistakes: MistakeEntry[] = [
  {
    id: 'm1', questionId: 'q2', question: demoQuestions[1],
    studentAnswer: '细胞膜由磷脂组成，蛋白质在上面。',
    errorReason: '描述过于简单，缺少 "双分子层" 和 "流动性" 的关键概念，未使用 IB 要求的科学术语。',
    correctApproach: '应该：1) 明确说明磷脂双分子层 (phospholipid bilayer)；2) 解释"流动"指磷脂和蛋白质的侧向运动；3) 解释"镶嵌"指蛋白质的分布模式。使用 integral/peripheral proteins 等术语。',
    similarQuestions: [demoQuestions[0]],
    date: '2026-02-10', resolved: false
  },
  {
    id: 'm2', questionId: 'q3', question: demoQuestions[2],
    studentAnswer: 'Mitosis produces two cells, meiosis produces four cells.',
    errorReason: '只描述了数量差异，缺少"遗传一致性 vs 遗传多样性"的关键对比，且未说明生物学意义。',
    correctApproach: '需要从多个维度比较：1) 子细胞数量；2) 遗传组成（identical vs unique）；3) 染色体数目变化；4) 生物学功能（growth vs reproduction）。使用对比性语言结构。',
    similarQuestions: [demoQuestions[3]],
    date: '2026-02-12', resolved: false
  },
];

const demoFlashcards: Flashcard[] = [
  { id: 'f1', term: 'Cell Theory 细胞理论', definition: '所有生物由一个或多个细胞组成；细胞是生命的基本单位；所有细胞来自已有细胞。', example: '显微镜下观察洋葱表皮细胞', unitId: 'bio-u1', subjectId: 'bio-hl', mastered: true },
  { id: 'f2', term: 'Phospholipid Bilayer 磷脂双分子层', definition: '由两层磷脂分子组成的细胞膜基本结构，疏水尾朝内，亲水头朝外。', example: '类比三明治结构', unitId: 'bio-u1', subjectId: 'bio-hl', mastered: false },
  { id: 'f3', term: 'Mitosis 有丝分裂', definition: '产生两个遗传相同的子细胞的细胞分裂方式，用于生长和修复。', example: '皮肤伤口愈合过程', unitId: 'bio-u1', subjectId: 'bio-hl', mastered: false },
  { id: 'f4', term: 'Meiosis 减数分裂', definition: '产生四个遗传不同的单倍体子细胞的细胞分裂方式，用于产生配子。', example: '精子和卵子的形成', unitId: 'bio-u1', subjectId: 'bio-hl', mastered: false },
  { id: 'f5', term: 'Active Transport 主动运输', definition: '利用ATP能量将物质逆浓度梯度运输across细胞膜的过程。', example: '肾小管对葡萄糖的重吸收', unitId: 'bio-u1', subjectId: 'bio-hl', mastered: true },
  { id: 'f6', term: 'Peptide Bond 肽键', definition: '两个氨基酸之间通过脱水缩合反应形成的共价键（-CO-NH-）。', unitId: 'bio-u2', subjectId: 'bio-hl', mastered: false },
];

const demoProgress: StudentProgress[] = [
  {
    subjectId: 'bio-hl', overallProgress: 52, lastStudyDate: '2026-02-13',
    unitProgress: { 'bio-u1': 78, 'bio-u2': 45, 'bio-u3': 20 },
    strengthAreas: ['细胞理论', '水的性质'], weakAreas: ['膜结构', '细胞分裂', '蛋白质结构'],
    predictedGrade: 5,
    assessmentScores: [
      { criterion: 'A', label: '知识与理解 Knowing & Understanding', description: '对科学知识的回忆与理解', maxScore: 8, currentScore: 6, strandDescriptors: ['回忆科学事实', '应用科学概念', '解决简单问题'] },
      { criterion: 'B', label: '探究与设计 Inquiring & Designing', description: '科学探究的设计与方法', maxScore: 8, currentScore: 5, strandDescriptors: ['提出研究问题', '设计实验方法', '评估实验可靠性'] },
      { criterion: 'C', label: '处理与评估 Processing & Evaluating', description: '数据处理与结论评估', maxScore: 8, currentScore: 5, strandDescriptors: ['整理数据', '分析结果', '评估结论有效性'] },
      { criterion: 'D', label: '科学态度 Reflecting on Impacts', description: '科学对社会的影响反思', maxScore: 8, currentScore: 7, strandDescriptors: ['讨论科学应用', '分析伦理影响', '评估全球意义'] },
    ]
  },
];

const demoDifficultyBreakdowns: DifficultyBreakdown[] = [
  {
    id: 'db1', knowledgePointId: 'kp3', title: '膜结构 Membrane Structure',
    breakdown: [
      '第一步：理解磷脂分子的双亲性（亲水头 + 疏水尾）',
      '第二步：想象磷脂自发排列成双分子层的过程',
      '第三步：认识膜蛋白的两种类型（integral 整合蛋白 vs peripheral 外周蛋白）',
      '第四步：理解"流动"指分子可以侧向移动，"镶嵌"指蛋白质像马赛克一样镶嵌其中'
    ],
    understandingTips: [
      '把细胞膜想象成一条流动的河（磷脂）上漂浮着各种船（蛋白质）',
      '画图是最好的记忆方式——IB 考试中经常要求画膜结构图',
      '重点记忆：cholesterol（胆固醇）调节膜流动性'
    ],
    ibAnswerStrategy: 'IB 评分要点：1) 使用正确术语（phospholipid bilayer, integral protein）；2) 解释结构与功能的关系；3) 6分题需要至少写6个要点；4) 画图加分但必须标注。',
    examples: ['模拟题：Draw and label the fluid mosaic model (4 marks)', '真题变式：Explain how the structure of the membrane is related to its function (6 marks)']
  },
  {
    id: 'db2', knowledgePointId: 'kp4', title: '细胞分裂 Cell Division',
    breakdown: [
      '第一步：区分有丝分裂（mitosis）和减数分裂（meiosis）的目的',
      '第二步：掌握有丝分裂的四个阶段（PMAT）',
      '第三步：理解减数分裂I和II的区别（同源染色体分离 vs 姐妹染色单体分离）',
      '第四步：认识交叉互换（crossing over）和独立分配（independent assortment）如何产生遗传变异'
    ],
    understandingTips: [
      'PMAT记忆法：Prophase-Metaphase-Anaphase-Telophase',
      '减数分裂的关键词：HALVING（减半）和 VARIATION（变异）',
      '画时间轴对比两种分裂过程，标注染色体数目变化'
    ],
    ibAnswerStrategy: 'IB 常考题型：Compare and contrast 题需要用表格或对比段落。评分关键：每个比较点需要同时说明两种分裂的情况，不能只描述一种。',
    examples: ['Distinguish between mitosis and meiosis (6 marks)', 'Explain the role of meiosis in generating genetic variation (8 marks)']
  }
];

// Demo study sessions for the past 7 days
const demoStudySessions: StudySession[] = [
  { id: 'ss1', date: '2026-02-08', subjectId: 'bio-hl', duration: 150, questionsCompleted: 15, questionsCorrect: 12, flashcardsReviewed: 8, unitId: 'bio-u1' },
  { id: 'ss2', date: '2026-02-09', subjectId: 'bio-hl', duration: 108, questionsCompleted: 12, questionsCorrect: 9, flashcardsReviewed: 5, unitId: 'bio-u1' },
  { id: 'ss3', date: '2026-02-10', subjectId: 'bio-hl', duration: 192, questionsCompleted: 22, questionsCorrect: 18, flashcardsReviewed: 12, unitId: 'bio-u2' },
  { id: 'ss4', date: '2026-02-11', subjectId: 'bio-hl', duration: 120, questionsCompleted: 18, questionsCorrect: 14, flashcardsReviewed: 6, unitId: 'bio-u1' },
  { id: 'ss5', date: '2026-02-12', subjectId: 'bio-hl', duration: 90, questionsCompleted: 10, questionsCorrect: 7, flashcardsReviewed: 4, unitId: 'bio-u2' },
  { id: 'ss6', date: '2026-02-13', subjectId: 'bio-hl', duration: 240, questionsCompleted: 35, questionsCorrect: 28, flashcardsReviewed: 18, unitId: 'bio-u1' },
  { id: 'ss7', date: '2026-02-14', subjectId: 'bio-hl', duration: 210, questionsCompleted: 28, questionsCorrect: 22, flashcardsReviewed: 15, unitId: 'bio-u3' },
];

// Demo question performance by tier
const demoQuestionPerformance: QuestionPerformance[] = [
  { tier: 'knowledge', total: 100, correct: 85, incorrect: 15 },
  { tier: 'application', total: 80, correct: 72, incorrect: 8 },
  { tier: 'analysis', total: 75, correct: 58, incorrect: 17 },
  { tier: 'evaluation', total: 60, correct: 45, incorrect: 15 },
];

interface AppState {
  // Active selections
  activeSubjectId: string;
  activeUnitId: string;
  activeModule: string;
  sidebarOpen: boolean;

  // Data
  subjects: Subject[];
  units: StudyUnit[];
  questions: Question[];
  mockTests: MockTest[];
  mistakes: MistakeEntry[];
  flashcards: Flashcard[];
  progress: StudentProgress[];
  uploads: UploadedMaterial[];
  difficultyBreakdowns: DifficultyBreakdown[];
  studySessions: StudySession[];
  questionPerformance: QuestionPerformance[];

  // Actions
  setActiveSubject: (id: string) => void;
  setActiveUnit: (id: string) => void;
  setActiveModule: (module: string) => void;
  toggleSidebar: () => void;
  toggleFlashcardMastery: (id: string) => void;
  toggleMistakeResolved: (id: string) => void;
  addUpload: (upload: UploadedMaterial) => void;
  addStudySession: (session: StudySession) => void;
}

export const useAppStore = create<AppState>((set) => ({
  activeSubjectId: 'bio-hl',
  activeUnitId: 'bio-u1',
  activeModule: 'dashboard',
  sidebarOpen: true,

  subjects: demoSubjects,
  units: demoUnits,
  questions: demoQuestions,
  mockTests: [],
  mistakes: demoMistakes,
  flashcards: demoFlashcards,
  progress: demoProgress,
  uploads: [],
  difficultyBreakdowns: demoDifficultyBreakdowns,
  studySessions: demoStudySessions,
  questionPerformance: demoQuestionPerformance,

  setActiveSubject: (id) => set({ activeSubjectId: id }),
  setActiveUnit: (id) => set({ activeUnitId: id }),
  setActiveModule: (module) => set({ activeModule: module }),
  toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
  toggleFlashcardMastery: (id) => set((s) => ({
    flashcards: s.flashcards.map(f => f.id === id ? { ...f, mastered: !f.mastered } : f)
  })),
  toggleMistakeResolved: (id) => set((s) => ({
    mistakes: s.mistakes.map(m => m.id === id ? { ...m, resolved: !m.resolved } : m)
  })),
  addUpload: (upload) => set((s) => ({ uploads: [...s.uploads, upload] })),
  addStudySession: (session) => set((s) => ({ studySessions: [...s.studySessions, session] })),
}));
